import React from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';

// eslint-disable-next-line no-restricted-imports
import { Button } from '@99designs/apps/spa/common/Button/Button';
import {
    ButtonGroup,
    ButtonGroupItem,
} from '@99designs/frontend-components/components/ButtonGroup';
import { formatMessage } from '@99designs/frontend-components/i18n';

import { DesignFigure } from '@99designs/apps/spa/common/DesignFigure';
import { Heading, Box, SubtleText } from '@99designs/ui';
import ContentBody from '@99designs/apps/spa/common/Layout/ContentBody';

import Poopskies from '@99designs/apps/spa/images/baby_figzy88.jpg';
import LostDog from '@99designs/apps/spa/images/dog_alinasink.jpg';
import ValentinoBaptista from '@99designs/apps/spa/images/valentino_baptista.png';
import Elephant from '@99designs/apps/spa/images/elephant_ganjarelex.jpg';
import Page from '@99designs/apps/spa/Page';
import useRouter from '@99designs/apps/spa/common/Routing/useRouter';
import { useAppConfig } from '@99designs/apps/spa/common/AppConfigContext';
import { breakpoints } from '@99designs/ui/settings';

const ErrorContainerBox = styled(Box)`
    grid-template-columns: 1fr 1fr;

    @media (min-width: ${breakpoints.small[0]}px) and (max-width: ${breakpoints.medium[1]}px) {
        grid-template-columns: 1fr;
    }
`;

const ErrorArtworkBox = styled(Box)`
    position: relative;
    grid-row: span 2;
`;

interface Props {
    errorCode: ErrorCodeKeys;
    error?: { message: string };
}

function FullscreenError({ errorCode, error }: Props) {
    const { displayErrors } = useAppConfig();
    const { staticContext } = useRouter();
    if (staticContext) {
        staticContext.statusCode = Number(errorCode);
    }

    return (
        <Page title={contentByErrorCode[errorCode].heading}>
            <Helmet
                bodyAttributes={{
                    class: 'max--desktop transparent-site-header',
                }}
            />
            <ContentBody>
                <ErrorContainerBox display="grid" paddingX="halfGutter">
                    <Box display="flex" flexDirection="column" justifyContent="center">
                        <Heading
                            accessibilityLevel={1}
                            withDivider
                            style={{ color: contentByErrorCode[errorCode].color }}
                            LEGACY_marginBottom="textLarge"
                        >
                            {contentByErrorCode[errorCode].heading}
                        </Heading>
                        <p>{contentByErrorCode[errorCode].message}</p>
                        {error && displayErrors ? <SubtleText>{error.message}</SubtleText> : null}
                    </Box>
                    <ErrorArtworkBox margin="auto" maxWidth="60vw">
                        <DesignFigure
                            imageSource={contentByErrorCode[errorCode].image}
                            attribution={formatMessage(__('by {designer}'), {
                                designer: contentByErrorCode[errorCode].imageAttribution,
                            })}
                            variations={['attribution-right']}
                        />
                    </ErrorArtworkBox>
                    <div>
                        <ButtonGroup variations={['responsive']}>
                            <ButtonGroupItem>
                                <Button
                                    href="/"
                                    variations={['jumbo', 'fluid', 'primary']}
                                    style={{
                                        backgroundColor: contentByErrorCode[errorCode].color,
                                        borderColor: contentByErrorCode[errorCode].color,
                                    }}
                                >
                                    {__('Get me outta here')}
                                </Button>
                            </ButtonGroupItem>
                            <ButtonGroupItem>
                                <Button
                                    href="/help"
                                    variations={['jumbo', 'fluid', 'secondary']}
                                    style={{
                                        backgroundColor: '#ffffff',
                                        borderColor: contentByErrorCode[errorCode].color,
                                        color: contentByErrorCode[errorCode].color,
                                    }}
                                >
                                    {__('Help')}
                                </Button>
                            </ButtonGroupItem>
                        </ButtonGroup>
                    </div>
                </ErrorContainerBox>
            </ContentBody>
        </Page>
    );
}

export default FullscreenError;

export type ErrorCodeKeys = '403' | '404' | '429' | '500';

type ErrorContentByCodeType = {
    [key in ErrorCodeKeys]: {
        message: string;
        heading: string;
        image: string;
        imageAttribution: string;
        color: string;
    };
};

const contentByErrorCode: ErrorContentByCodeType = {
    403: {
        heading: __('Poopskies'),
        message: __('This is not where I want to be!'),
        image: Poopskies,
        imageAttribution: 'Figzy88',
        color: '#4DC0E3',
    },
    404: {
        heading: __('Lost your way?'),
        message: __('This is not where I want to be!'),
        image: LostDog,
        imageAttribution: 'ALINAsINK',
        color: '#F36C78',
    },
    429: {
        heading: __('Slow down!'),
        message: __(
            "We're experiencing a flood of traffic from the same IP address as your browser, try again later"
        ),
        image: ValentinoBaptista,
        imageAttribution: 'Valentino Baptista',
        color: '#DD533C',
    },
    500: {
        heading: __('Eeeeeeek!'),
        message: __(
            'It seems there was a problem. The 99designs development team has been notified and are likely addressing the problem at this very minute. Sit tight, grab a coffee, and hit refresh in a few minutes.'
        ),
        image: Elephant,
        imageAttribution: 'ganjarelex',
        color: '#C1B946',
    },
};
